import React from "react";

import dayjs from "dayjs";

import {CircularProgress, Link} from "@mui/joy";

// components
import BaseApi from "../../utils/BaseApi";
import CommonDetail from "../Common/CommonDetail";
import withRouter from "../../utils/withRouter";
import UserSubscriberContext from "../../context/UserSubscriberContext";
import retrievePermission from "../../utils/retrievePermission";
import CommonEditModal from "../Common/CommonEditModal";

// Tabs
import OpportunityDetailHeader from "./OpportunityDetailHeader";
import DeliveryTab from "../Delivery/DeliveryTab";
import TeamTab from "./Tabs/TeamTab";
import ContactTeamTab from "./Tabs/ContactTeamTab";
import AttachmentsTab from "../Attachment/AttachmentsTab";
import TasksTab from "../Task/TasksTab";
import TasksCalendarTab from "../Task/TasksCalendarTab"
import EstimationTab from "../Estimation/EstimationTab";
import QualificatorTab from "../Qualificator/QualificatorTab";
import ContractTab from "../Contract/ContractTab";
import PaymentCertificateTab from "./Tabs/PaymentCertificateTab";
import NewQuoteTab from "../NewQuote/NewQuoteTab";
import LetterTab from "../Letter/LetterTab";
import CommonInfoTab from "../Common/CommonInfoTab";
import EvaluationTabNew from "./Tabs/EvaluationTabNew";
import ConfidentialAttachmentTab from "../ConfidentialAttachment/ConfidentialAttachmentTab";
import SliceTab from "../Slice/SliceTab";
import EmailTab from "../Email/EmailTab";
import CallsTab from "./Tabs/CallsTab";

// icons
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LocalPizzaIcon from '@mui/icons-material/LocalPizza';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScaleIcon from '@mui/icons-material/Scale';
import GavelIcon from '@mui/icons-material/Gavel';
import VideoChatIcon from '@mui/icons-material/VideoChat';
import LaunchIcon from "@mui/icons-material/Launch";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import SavedSearchIcon from '@mui/icons-material/SavedSearch';
import LockIcon from '@mui/icons-material/Lock';
import SummarizeIcon from '@mui/icons-material/Summarize';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';


class OpportunityDetail extends React.Component {
    static contextType = UserSubscriberContext

    constructor(props) {
        super(props);
        this.state = {
            opportunity: null,
            updatePermission: null,
            paymentPermission: null,
            certificatePermission: null,
            quotePermission: null,
            confidentialAttachmentPermission: null
        };
    }

    async componentDidMount() {
        await this.retrieveUpdatePermissions();
        await this.retrieveOpportunity();
    }

    async retrieveUpdatePermissions() {
        let paymentPermission = await retrievePermission(
            'OppPaymentViewSet',
            'list',
            this.context.subscriberObject
        );
        let certificatePermission = await retrievePermission(
            'CertificateViewSet',
            'list',
            this.context.subscriberObject
        );
        let quotePermission = await retrievePermission(
            'NewQuoteViewSet',
            'list',
            this.context.subscriberObject
        );
        let updatePermission = await retrievePermission(
            'OpportunityViewSet',
            'partial_update',
            this.context.subscriberObject
        );
        let confidentialAttachmentPermission = await retrievePermission(
            'ConfidentialAttachmentViewSet',
            'list',
            this.context.subscriberObject
        );
        this.setState(
            {
                paymentPermission: paymentPermission,
                certificatePermission: certificatePermission,
                updatePermission: updatePermission,
                quotePermission: quotePermission,
                confidentialAttachmentPermission: confidentialAttachmentPermission,
            }
        );
    }

    async componentDidUpdate(prevProps, prevState, snapshot){
        if (prevProps !== this.props){
            await this.retrieveOpportunity();
        }
    }

    async retrieveOpportunity() {
        this.setState(
            {
                opportunity: null
            }
        );
        const url = `opportunity/${this.props.params.opportunityId}`;
        const api = new BaseApi();
        let response = await api.get(url, {});
        this.setState(
            {
                opportunity: response.data
            }
        );
    }

    render() {
        if (!this.state.opportunity) {
            return (<CircularProgress/>)
        }
        return (
            <CommonDetail
                appName={'Opportunity'}
                appId={this.state.opportunity.id}
                header={{
                    component: OpportunityDetailHeader,
                    componentProps: {
                        opportunity: this.state.opportunity
                    },
                    leftOverflow: {
                        icon: null,
                        title: this.state.opportunity.status_human,
                        color: this.state.opportunity.status_color,
                        component: <CommonEditModal
                            overlay={true}
                            fields={
                                [
                                    'status',
                                ]
                            }
                            baseUrl={'opportunity'}
                            obj={this.state.opportunity}
                            buttonIconOnly={true}
                            callBack={this.retrieveOpportunity.bind(this)}
                            permission={this.state.updatePermission}
                            deniedPermissionComponent={<></>}
                        />,
                    },
                    rightOverflow: {
                        icon: <VideoChatIcon />,
                        title: 'ВКС',
                        color: 'primary',
                        component: <Link
                            level={'body-md'}
                            href={`https://meet.multiphysics.ru/${this.state.opportunity.uuid}`}
                            target={'_blank'}
                            color="inherit"
                            underline="none"
                        >
                            <LaunchIcon />
                        </Link>
                    }
                }}
                extraComponents={[
                    {
                        color: 'warning',
                        hidden: !this.context.userObject.is_superuser,
                        component: CommonInfoTab,
                        props: {
                            callBack: this.retrieveOpportunity.bind(this),
                            flat: true,
                            groups: [
                                {
                                    flat: true,
                                    fields: [
                                        {
                                            title: 'Выручка',
                                            value: this.state.opportunity.forecast_revenue,
                                            xs: 12,
                                            sm: 4,
                                            md: 2,
                                            lg:  2,
                                            edit: {
                                                permission: this.context.userObject.is_superuser,
                                                fields: ['forecast_revenue',],
                                                baseUrl: 'opportunity',
                                                obj: this.state.opportunity,
                                            }
                                        },
                                        {
                                            title: 'Предоплата %',
                                            value: this.state.opportunity.forecast_prepay_rate,
                                            xs: 12,
                                            sm: 4,
                                            md: 2,
                                            lg: 2,
                                            edit: {
                                                permission: this.context.userObject.is_superuser,
                                                fields: ['forecast_prepay_rate',],
                                                baseUrl: 'opportunity',
                                                obj: this.state.opportunity,
                                            }
                                        },
                                        {
                                            title: 'Загрузка (дней)',
                                            value: this.state.opportunity.forecast_workdays,
                                            xs: 12,
                                            sm: 4,
                                            md: 2,
                                            lg: 2,
                                            edit: {
                                                permission: this.context.userObject.is_superuser,
                                                fields: ['forecast_workdays',],
                                                baseUrl: 'opportunity',
                                                obj: this.state.opportunity,
                                            }
                                        },
                                        {
                                            title: 'Исполнителей',
                                            value: this.state.opportunity.forecast_workers,
                                            xs: 12,
                                            sm: 4,
                                            md: 2,
                                            lg: 2,
                                            edit: {
                                                permission: this.context.userObject.is_superuser,
                                                fields: ['forecast_workers',],
                                                baseUrl: 'opportunity',
                                                obj: this.state.opportunity,
                                            }
                                        },
                                        {
                                            title: 'Дата начала',
                                            value: this.state.opportunity.forecast_start_date?dayjs(this.state.opportunity.forecast_start_date).format('DD MMM YYYY'):null,
                                            xs: 12,
                                            sm: 4,
                                            md: 2,
                                            lg: 2,
                                            edit: {
                                                permission: this.context.userObject.is_superuser,
                                                fields: ['forecast_start_date',],
                                                baseUrl: 'opportunity',
                                                obj: this.state.opportunity,
                                            }
                                        },
                                        {
                                            title: 'Хвост',
                                            value: this.state.opportunity.forecast_tail,
                                            xs: 12,
                                            sm: 4,
                                            md: 2,
                                            lg: 2,
                                            edit: {
                                                permission: this.context.userObject.is_superuser,
                                                fields: ['forecast_tail',],
                                                baseUrl: 'opportunity',
                                                obj: this.state.opportunity,
                                            }
                                        },
                                    ]
                                }
                            ]
                        }
                    },
                ]}
                defaultTabValue={'delivery'}
                tabComponents={[
                    {
                        title: 'Бизнес-процессы',
                        value: 'delivery',
                        icon: AccountTreeIcon,
                        component: <DeliveryTab
                            opportunity={this.state.opportunity}
                        />,
                    },
                    {
                        title: 'Команда',
                        value: 'team',
                        icon: GroupsIcon,
                        component: <TeamTab
                            opportunity={this.state.opportunity}
                        />,
                    },
                    {
                        title: 'Команда заказчика',
                        value: 'contact-team',
                        icon: PeopleOutlineIcon,
                        component: <ContactTeamTab
                            opportunity={this.state.opportunity}
                        />,
                    },
                    {
                        title: 'Вложения',
                        value: 'attachment',
                        icon: AttachFileIcon,
                        component: <AttachmentsTab
                            appId={this.state.opportunity.id}
                            appName={'Opportunity'}
                        />
                    },
                    {
                        title: 'Email',
                        value: 'email',
                        icon: EmailIcon,
                        component: <EmailTab
                            entity={'Opportunity'}
                            entity_id={this.state.opportunity.id}
                        />,
                    },
                    {
                        title: 'Звонки',
                        value: 'calls',
                        icon: PhoneIcon,
                        component: <CallsTab
                            opportunity={this.state.opportunity}
                        />,
                    },
                    {
                        title: 'Квалификатор',
                        value: 'qualificator',
                        icon: ScaleIcon,
                        component: <QualificatorTab
                            qualificatorId={this.state.opportunity.qualificator}
                            opportunity={this.state.opportunity}
                        />,
                    },
                    {
                        title: 'Трудоемкость',
                        value: 'estimation',
                        icon: LocalPizzaIcon,
                        component: <EstimationTab
                            opportunity={this.state.opportunity}
                        />,
                    },
                    {
                        title: 'Задачи',
                        value: 'task',
                        icon: AssignmentIcon,
                        component: <TasksTab
                            create={true}
                            opportunity={this.state.opportunity}
                        />,
                    },
                    {
                        title: 'Календарь',
                        value: 'calendar',
                        icon: CalendarMonthIcon,
                        component: <TasksCalendarTab
                            opportunity={this.state.opportunity}
                        />,
                    },
                    {
                        title: 'Оценка проекта',
                        value: 'evaluation',
                        icon: SavedSearchIcon,
                        component: <EvaluationTabNew
                            opportunity={this.state.opportunity}
                        />,
                    },
                    {
                        title: 'ТКП',
                        value: 'quote',
                        icon: AccountBalanceWalletIcon,
                        component: <NewQuoteTab
                            opportunity={this.state.opportunity}
                        />,
                        hidden: !this.state.quotePermission
                    },
                    {
                        title: 'Договор',
                        value: 'contract',
                        icon: GavelIcon,
                        component: <ContractTab
                            opportunity={this.state.opportunity}
                        />,
                    },
                    {
                        title: 'Исходящие письма',
                        value: 'addresses',
                        icon: ForwardToInboxIcon,
                        component: <LetterTab
                            opportunity={this.state.opportunity}
                            account={this.state.opportunity.account}
                        />,
                    },
                    {
                        title: 'Платежи и Акты',
                        value: 'paymentCertificate',
                        icon: CurrencyRubleIcon,
                        component: <PaymentCertificateTab
                            opportunity={this.state.opportunity}
                        />,
                        hidden: !this.state.certificatePermission && !this.state.paymentPermission
                    },
                    {
                        title: 'Конф. вложения',
                        value: 'confidentialAttachment',
                        icon: LockIcon,
                        component: <ConfidentialAttachmentTab
                            appId={this.state.opportunity.id}
                            appName={'Opportunity'}
                        />,
                        hidden: !this.state.confidentialAttachmentPermission
                    },
                    {
                        title: 'Срезы',
                        value: 'slice',
                        icon: SummarizeIcon,
                        component: <SliceTab
                            opportunity={this.state.opportunity}
                        />,
                    },
                ]}
            />
        )
    }
}

export default withRouter(OpportunityDetail);
